import { useState, useEffect } from "react";
import axios from "axios";

const API = "https://avebackend.onrender.com/api";

export default function AnalyticsPage() {
  const [data, setData]       = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [days, setDays]       = useState(7);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  async function load(d = days) {
    setLoading(true); setError("");
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/analytics/website?days=${d}`),
        axios.get(`${API}/analytics/realtime`),
      ]);
      if (!r1.data.success) { setError(r1.data.error || "GA4 fetch failed"); setLoading(false); return; }
      setData(r1.data);
      setRealtime(r2.data);
    } catch(e) { setError(e.message); }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  function changeDays(d) { setDays(d); load(d); }

  const s = data?.summary || {};
  const pages = data?.top_pages || [];
  const sources = data?.traffic_sources || [];
  const countries = data?.countries || [];
  const trend = data?.daily_trend || [];

  const maxUsers = trend.length ? Math.max(...trend.map(t => t.users), 1) : 1;

  return (
    <div style={{ padding: "24px" }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"20px" }}>
        <div>
          <h2 style={{ margin:0, fontSize:"20px" }}>📊 Website Analytics</h2>
          <p style={{ margin:"4px 0 0", color:"#666", fontSize:"13px" }}>
            Live data from Google Analytics 4 · aventrixtechnologies.com
          </p>
        </div>
        <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
          {[7,14,28].map(d => (
            <button key={d} onClick={() => changeDays(d)}
              style={{ padding:"6px 14px", borderRadius:"6px", border:"1px solid #ddd", cursor:"pointer",
                background: days===d ? "#3B1670" : "#fff", color: days===d ? "#fff" : "#333", fontSize:"13px" }}>
              {d}d
            </button>
          ))}
          <button onClick={() => load()} disabled={loading}
            style={{ padding:"6px 14px", borderRadius:"6px", border:"1px solid #3B1670", background:"#3B1670",
              color:"#fff", cursor:"pointer", fontSize:"13px" }}>
            {loading ? "Loading…" : "↻ Refresh"}
          </button>
        </div>
      </div>

      {/* Realtime strip */}
      {realtime?.success && (
        <div style={{ background:"linear-gradient(135deg,#3B1670,#5B21B6)", borderRadius:"10px",
          padding:"14px 20px", marginBottom:"20px", display:"flex", alignItems:"center", gap:"12px" }}>
          <span style={{ fontSize:"22px" }}>🟢</span>
          <div>
            <div style={{ color:"#fff", fontWeight:700, fontSize:"22px" }}>
              {realtime.total_active_now} <span style={{ fontSize:"14px", fontWeight:400 }}>active right now</span>
            </div>
            <div style={{ color:"#C4B5FD", fontSize:"13px" }}>
              {realtime.by_country?.map(c => `${c.country} (${c.users})`).join(" · ")}
            </div>
          </div>
        </div>
      )}

      {error && (
        <div style={{ background:"#FFF0F0", border:"1px solid #FFB3B3", borderRadius:"8px",
          padding:"14px", marginBottom:"20px", color:"#B91C1C", fontSize:"14px" }}>
          ⚠ GA4 Error: {error}
          <div style={{ marginTop:"8px", fontSize:"12px", color:"#666" }}>
            Make sure GA_SERVICE_ACCOUNT_JSON is set in Render environment variables.
          </div>
        </div>
      )}

      {data && (
        <>
          {/* Summary cards */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:"12px", marginBottom:"20px" }}>
            {[
              { label:"Active Users", value: s.active_users, icon:"👤" },
              { label:"New Users",    value: s.new_users,    icon:"✨" },
              { label:"Sessions",     value: s.sessions,     icon:"🔁" },
              { label:"Page Views",   value: s.page_views,   icon:"📄" },
              { label:"Avg Duration", value: `${s.avg_session_sec}s`, icon:"⏱" },
              { label:"Bounce Rate",  value: `${s.bounce_rate}%`,
                color: s.bounce_rate > 60 ? "#DC2626" : s.bounce_rate > 40 ? "#D97706" : "#059669", icon:"↩" },
            ].map(c => (
              <div key={c.label} style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:"10px",
                padding:"16px 14px", textAlign:"center", boxShadow:"0 1px 3px rgba(0,0,0,0.05)" }}>
                <div style={{ fontSize:"22px", marginBottom:"4px" }}>{c.icon}</div>
                <div style={{ fontSize:"22px", fontWeight:700, color: c.color || "#1a1a2e" }}>{c.value ?? 0}</div>
                <div style={{ fontSize:"11px", color:"#6B7280", marginTop:"4px" }}>{c.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"16px", marginBottom:"16px" }}>
            {/* Daily trend chart */}
            <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:"10px", padding:"18px" }}>
              <h4 style={{ margin:"0 0 14px", fontSize:"14px", color:"#374151" }}>📈 Daily Users ({days}d)</h4>
              <div style={{ display:"flex", alignItems:"flex-end", gap:"6px", height:"100px" }}>
                {trend.map((t, i) => (
                  <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:"4px" }}>
                    <div style={{ fontSize:"10px", color:"#6B7280" }}>{t.users}</div>
                    <div style={{ width:"100%", borderRadius:"3px 3px 0 0",
                      background: "linear-gradient(180deg,#3B1670,#7C3AED)",
                      height: `${Math.max((t.users / maxUsers) * 80, 4)}px` }}></div>
                    <div style={{ fontSize:"9px", color:"#9CA3AF", writingMode:"vertical-rl", transform:"rotate(180deg)" }}>
                      {t.date.slice(5)}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Traffic sources */}
            <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:"10px", padding:"18px" }}>
              <h4 style={{ margin:"0 0 14px", fontSize:"14px", color:"#374151" }}>🔗 Traffic Sources</h4>
              {sources.slice(0,6).map((s,i) => (
                <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
                  padding:"7px 0", borderBottom: i < sources.length-1 ? "1px solid #F3F4F6" : "none" }}>
                  <div>
                    <span style={{ fontSize:"13px", fontWeight:500 }}>{s.source}</span>
                    <span style={{ fontSize:"11px", color:"#9CA3AF", marginLeft:"6px" }}>/{s.medium}</span>
                  </div>
                  <div style={{ display:"flex", gap:"10px" }}>
                    <span style={{ fontSize:"12px", color:"#6B7280" }}>{s.sessions} sessions</span>
                    <span style={{ fontSize:"12px", fontWeight:600, color:"#3B1670" }}>{s.users} users</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1.5fr 1fr", gap:"16px" }}>
            {/* Top pages */}
            <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:"10px", padding:"18px" }}>
              <h4 style={{ margin:"0 0 14px", fontSize:"14px", color:"#374151" }}>📄 Top Pages</h4>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"13px" }}>
                <thead>
                  <tr style={{ color:"#9CA3AF", fontSize:"11px" }}>
                    <th style={{ textAlign:"left", paddingBottom:"8px", fontWeight:500 }}>Page</th>
                    <th style={{ textAlign:"right", paddingBottom:"8px", fontWeight:500 }}>Views</th>
                    <th style={{ textAlign:"right", paddingBottom:"8px", fontWeight:500 }}>Users</th>
                    <th style={{ textAlign:"right", paddingBottom:"8px", fontWeight:500 }}>Bounce</th>
                  </tr>
                </thead>
                <tbody>
                  {pages.map((p,i) => (
                    <tr key={i} style={{ borderTop:"1px solid #F3F4F6" }}>
                      <td style={{ padding:"8px 0", maxWidth:"200px", overflow:"hidden",
                        textOverflow:"ellipsis", whiteSpace:"nowrap", color:
                        p.page.includes("Pricing") || p.page.includes("Contact") ? "#3B1670" : "#374151",
                        fontWeight: p.page.includes("Pricing") || p.page.includes("Contact") ? 600 : 400 }}>
                        {p.page.includes("Pricing") || p.page.includes("Contact") ? "🔥 " : ""}{p.page.slice(0,35)}
                      </td>
                      <td style={{ textAlign:"right", padding:"8px 0", color:"#6B7280" }}>{p.views}</td>
                      <td style={{ textAlign:"right", padding:"8px 0", fontWeight:600 }}>{p.users}</td>
                      <td style={{ textAlign:"right", padding:"8px 0",
                        color: p.bounce_rate > 60 ? "#DC2626" : p.bounce_rate > 40 ? "#D97706" : "#059669" }}>
                        {p.bounce_rate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Countries */}
            <div style={{ background:"#fff", border:"1px solid #E5E7EB", borderRadius:"10px", padding:"18px" }}>
              <h4 style={{ margin:"0 0 14px", fontSize:"14px", color:"#374151" }}>🌍 Visitors by Country</h4>
              {countries.map((c, i) => {
                const max = countries[0]?.users || 1;
                return (
                  <div key={i} style={{ marginBottom:"10px" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:"3px" }}>
                      <span style={{ fontSize:"13px" }}>{c.country}</span>
                      <span style={{ fontSize:"13px", fontWeight:600 }}>{c.users}</span>
                    </div>
                    <div style={{ background:"#F3F4F6", borderRadius:"4px", height:"6px" }}>
                      <div style={{ background:"linear-gradient(90deg,#3B1670,#7C3AED)",
                        borderRadius:"4px", height:"6px", width:`${(c.users/max)*100}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {loading && !data && (
        <div style={{ textAlign:"center", padding:"60px", color:"#6B7280" }}>
          Loading analytics from Google Analytics 4…
        </div>
      )}
    </div>
  );
}
